import { Fragment, useCallback, useId, useMemo, useRef, useState } from "react";
import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent } from "react";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { Combobox, Kbd, useCombobox } from "@mantine/core";
import type { ComboboxStore } from "@mantine/core";
import type {
    CustomSelectCreatableFactory,
    IProps,
    ISelectCreatableGroup,
    ISelectCreatableOption,
} from "./select-creatable.types";
import { componentWrapper } from "../../../utilities";
import { filterOptions, findOptionByLabel, getAvailableOptions, groupOptions } from "./select-creatable.utilities";
import { DIVIDER_SPACING, NO_ITEMS_FOUND_LABEL } from "./select-creatable.constants";
import { MENU_AVAILABLE_HEIGHT, ROW_LABEL_SIZE, SEARCHABLE_MENU_MAX_HEIGHT } from "../../auto-menu/auto-menu.constants";
import { MENU_DROPDOWN_OFFSET, MENU_ITEM_PADDING } from "../../menu/menu.constants";
import { OVERLAY_FIT_MIDDLEWARES } from "../../popover";
import { ActionIcon } from "../../action-icon";
import { Box } from "../../box";
import { UnstyledButton } from "../../button";
import { Divider } from "../../divider";
import { DropdownIcon } from "../../dropdown-icon";
import { Group } from "../../group";
import { Highlight } from "../../highlight";
import { LoaderIcon } from "../../loader-icon";
import { ScrollArea } from "../../scroll-area";
import { Stack } from "../../stack";
import { Text } from "../../text";
import { TextInput } from "../../text-input";
import menuStyles from "../../menu/menu.module.scss";
import selectMenuStyles from "../select-menu/select-menu.module.scss";
import styles from "./select-creatable.module.scss";

/**
 * A searchable select that can also create a new option from the typed text. Typing filters the options, and when no
 * option's label equals the text a "Create" row offers to create it.
 * @param props.options The options to choose from.
 * @param props.value The selected option's value. A value matching no option is displayed as raw text. A value whose
 * option sits in a group not listed in `groups` still shows that option's label.
 * @param props.onChange Called with the value and the option when an existing option is picked.
 * @param props.onCreate Called with the trimmed typed text when the "Create" row is picked. When it returns a promise,
 * the create row and the input show a loader until it settles, the typed text stays even if the list closes, and the
 * list closes once it resolves.
 * @param props.groups Group names, in display order. When set, options are listed under a collapsible header per group.
 * @param props.renderOption Renders an option's content in place of its highlighted label.
 * @param props.variant The input's variant. Defaults to "filled".
 * @param props.disabled Disables the input. The dropdown never opens.
 * @param props.readOnly Makes the input read-only. The dropdown never opens and the keyboard does not navigate it.
 * @param props.loading Shows a loader in place of the dropdown icon and keeps the input read-only while the options load.
 * @param props.noItemsFoundLabel Shown in the open list when there are no options to show and nothing to create.
 * @param props.onBlur Called after the input blurs, once the typed text has been discarded, or kept while a create is
 * pending.
 * @param props.onClick Called after the input is clicked, once the dropdown has opened.
 * @param props.onKeyDown Called with every key press on the input, before the component handles it.
 * @returns {JSX.Element} The SelectCreatable component.
 */
const SelectCreatable = componentWrapper<CustomSelectCreatableFactory, IProps>(
    "SelectCreatable",
    (
        {
            options,
            value,
            onChange,
            onCreate,
            groups,
            renderOption,
            variant = "filled",
            disabled,
            readOnly,
            loading = false,
            noItemsFoundLabel = NO_ITEMS_FOUND_LABEL,
            onBlur,
            onClick,
            onKeyDown,
            ...otherProps
        },
        ref
    ) => {
        const isReadOnly = useMemo(() => !!readOnly || loading, [readOnly, loading]);
        const id = useId();
        const createOptionValue = `select-creatable-create-${id}`;

        const selectedLabel = useMemo(
            () => options.find((option) => option.value === value)?.label ?? value ?? "",
            [options, value]
        );

        const [search, setSearch] = useState(selectedLabel);
        const [previousSelectedLabel, setPreviousSelectedLabel] = useState(selectedLabel);
        const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
        const [isCreating, setIsCreating] = useState(false);
        const isBusy = useMemo(() => loading || isCreating, [loading, isCreating]);
        // Read by the close and blur handlers, which keep the text being created until the create settles. Set where
        // the create starts and settles, so it is already current when a settled create closes the list.
        const isCreatingRef = useRef(false);

        // Show the new selection whenever it changes, for example once a created option has been added.
        if (selectedLabel !== previousSelectedLabel) {
            setPreviousSelectedLabel(selectedLabel);
            setSearch(selectedLabel);
        }

        // Read through refs so the close handler, and the store callbacks built on it, keep one identity.
        const selectedLabelRef = useRef(selectedLabel);
        selectedLabelRef.current = selectedLabel;
        const comboboxRef = useRef<ComboboxStore | null>(null);
        // Counts the times the list has closed, so a pending create can tell whether the user closed it meanwhile.
        const closeCountRef = useRef(0);

        const handleDropdownClose = useCallback(() => {
            closeCountRef.current += 1;
            comboboxRef.current?.resetSelectedOption();
            if (!isCreatingRef.current) {
                setSearch(selectedLabelRef.current);
            }
        }, []);

        const combobox = useCombobox({ onDropdownClose: handleDropdownClose });
        comboboxRef.current = combobox;
        const { closeDropdown, dropdownOpened, getSelectedOptionIndex, openDropdown, resetSelectedOption } = combobox;

        // The list stays unfiltered until the text differs from the selection, like Mantine's searchable Select.
        const isSearching = useMemo(() => search !== selectedLabel, [search, selectedLabel]);
        const trimmedSearch = useMemo(() => search.trim(), [search]);
        const filterText = useMemo(() => (isSearching ? trimmedSearch : ""), [isSearching, trimmedSearch]);
        const isFiltering = useMemo(() => filterText.length > 0, [filterText]);

        const availableOptions = useMemo(() => getAvailableOptions(options, groups), [options, groups]);
        const visibleOptions = useMemo(
            () => (isFiltering ? filterOptions(availableOptions, filterText) : availableOptions),
            [isFiltering, filterText, availableOptions]
        );
        const visibleGroups = useMemo(
            () => (typeof groups === "undefined" ? undefined : groupOptions(visibleOptions, groups)),
            [visibleOptions, groups]
        );
        const exactMatch = useMemo(
            () => findOptionByLabel(availableOptions, trimmedSearch),
            [availableOptions, trimmedSearch]
        );

        const isCreateShown = useMemo(
            () => isSearching && trimmedSearch.length > 0 && typeof exactMatch === "undefined",
            [isSearching, trimmedSearch, exactMatch]
        );
        const isDropdownHidden = useMemo(() => !!disabled || !!isReadOnly, [disabled, isReadOnly]);
        const isDropdownShown = useMemo(() => dropdownOpened && !isDropdownHidden, [dropdownOpened, isDropdownHidden]);

        const selectOption = useCallback(
            (option: ISelectCreatableOption) => {
                onChange(option.value, option);
                closeDropdown();
            },
            [onChange, closeDropdown]
        );

        // An asynchronous create keeps the list open with a loader until it settles. It closes the list once it resolves,
        // and leaves the typed text in place if it rejects so the user can try again. If the user closed the list while it
        // was pending, the text stays until the create settles and then shows the selection; a list reopened since stays
        // open.
        const create = useCallback(() => {
            const result = onCreate(trimmedSearch);
            if (!result || typeof result.then !== "function") {
                closeDropdown();
                return;
            }
            const closeCountAtCreate = closeCountRef.current;
            isCreatingRef.current = true;
            setIsCreating(true);
            Promise.resolve(result).then(
                () => {
                    isCreatingRef.current = false;
                    setIsCreating(false);
                    if (closeCountRef.current === closeCountAtCreate) {
                        comboboxRef.current?.closeDropdown();
                    } else {
                        // The list was closed meanwhile, which kept the created text on show. Show the selection now.
                        setSearch(selectedLabelRef.current);
                    }
                },
                () => {
                    isCreatingRef.current = false;
                    setIsCreating(false);
                    // The list was closed meanwhile, so it no longer shows the text to retry. Show the selection again.
                    if (closeCountRef.current !== closeCountAtCreate) {
                        setSearch(selectedLabelRef.current);
                    }
                }
            );
        }, [onCreate, trimmedSearch, closeDropdown]);

        const handleOptionSubmit = useCallback(
            (optionValue: string) => {
                // Nothing else can be picked while a create is in flight.
                if (isCreating) {
                    return;
                }
                if (optionValue === createOptionValue) {
                    create();
                    return;
                }
                const option = availableOptions.find((item) => item.value === optionValue);
                if (option && !option.disabled) {
                    selectOption(option);
                }
            },
            [isCreating, createOptionValue, create, availableOptions, selectOption]
        );

        const toggleGroup = useCallback(
            (name: string) => {
                setCollapsedGroups((current) =>
                    current.includes(name) ? current.filter((group) => group !== name) : [...current, name]
                );
                resetSelectedOption();
            },
            [resetSelectedOption]
        );

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                // The text being created stays put until the create settles.
                if (isCreating) {
                    return;
                }
                setSearch(event.currentTarget.value);
                openDropdown();
                resetSelectedOption();
            },
            [isCreating, openDropdown, resetSelectedOption]
        );

        const handleClick = useCallback(
            (event: MouseEvent<HTMLInputElement>) => {
                if (!isReadOnly) {
                    openDropdown();
                }
                onClick?.(event);
            },
            [isReadOnly, openDropdown, onClick]
        );

        const handleBlur = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                closeDropdown();
                // Also covers a blur while the dropdown is already closed, when the close handler does not run. The text
                // being created stays, with the loader, until the create settles.
                if (!isCreatingRef.current) {
                    setSearch(selectedLabelRef.current);
                }
                onBlur?.(event);
            },
            [closeDropdown, onBlur]
        );

        // This runs before Combobox.Target's own handler, which clicks the highlighted option on Enter. Enter never
        // submits a surrounding form while the list is showing. With no option highlighted, Enter picks the option the
        // typed text names, or creates one, and with nothing typed it just closes the list.
        const handleKeyDown = useCallback(
            (event: KeyboardEvent<HTMLInputElement>) => {
                onKeyDown?.(event);
                const isEnter = event.key === "Enter" && !event.nativeEvent.isComposing;
                if (!isEnter || !isDropdownShown) {
                    return;
                }
                event.preventDefault();
                if (isCreating || getSelectedOptionIndex() !== -1) {
                    return;
                }
                if (!isSearching) {
                    closeDropdown();
                } else if (exactMatch && !exactMatch.disabled) {
                    selectOption(exactMatch);
                } else if (isCreateShown) {
                    create();
                }
            },
            [
                onKeyDown,
                isDropdownShown,
                isCreating,
                getSelectedOptionIndex,
                isSearching,
                closeDropdown,
                exactMatch,
                selectOption,
                isCreateShown,
                create,
            ]
        );

        const renderOptionRow = useCallback(
            (option: ISelectCreatableOption) => (
                <Combobox.Option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    active={option.value === value}
                    p={MENU_ITEM_PADDING}
                    bdrs="md"
                    data-testid={`select-creatable-option-${option.value}`}
                >
                    {renderOption ? (
                        renderOption({ option, search: filterText })
                    ) : (
                        <Stack gap="xs">
                            <Highlight size={ROW_LABEL_SIZE} color="blue" highlight={filterText}>
                                {option.label}
                            </Highlight>
                            {option.description && (
                                <Text size="xs" dimmed>
                                    {option.description}
                                </Text>
                            )}
                        </Stack>
                    )}
                </Combobox.Option>
            ),
            [value, renderOption, filterText]
        );

        // While the list is filtered every group shows its matches, so the headers can't collapse them. A group's
        // collapsed state is kept and applies again once the filter clears.
        const renderGroup = useCallback(
            ({ name, options: groupedOptions }: ISelectCreatableGroup, index: number) => {
                const isExpanded = isFiltering || !collapsedGroups.includes(name);
                const headerId = `${id}-group-${index}`;
                return (
                    <Fragment key={name}>
                        {index > 0 && <Divider my={DIVIDER_SPACING} />}
                        <Box role="group" aria-labelledby={headerId}>
                            <UnstyledButton
                                id={headerId}
                                w="100%"
                                px={MENU_ITEM_PADDING}
                                py="sm"
                                tabIndex={-1}
                                disabled={isFiltering}
                                aria-expanded={isExpanded}
                                data-testid={`select-creatable-group-${name}`}
                                onClick={() => toggleGroup(name)}
                            >
                                <Group gap="sm" justify="space-between" wrap="nowrap">
                                    <Text size="xs" variant="medium" dimmed>
                                        {`${name} (${groupedOptions.length.toLocaleString()})`}
                                    </Text>
                                    <DropdownIcon isOpen={isExpanded} />
                                </Group>
                            </UnstyledButton>
                            {isExpanded && groupedOptions.map(renderOptionRow)}
                        </Box>
                    </Fragment>
                );
            },
            [isFiltering, collapsedGroups, id, toggleGroup, renderOptionRow]
        );

        return (
            <Combobox
                store={combobox}
                readOnly={isReadOnly}
                position="bottom-start"
                offset={MENU_DROPDOWN_OFFSET}
                middlewares={OVERLAY_FIT_MIDDLEWARES}
                classNames={{ dropdown: menuStyles["dropdown"], option: styles["select-creatable-option"] }}
                onOptionSubmit={handleOptionSubmit}
            >
                {/* Given here rather than on the input, since the target's own aria props would replace it there. */}
                <Combobox.Target targetType="input" autoComplete="off" aria-expanded={isDropdownShown}>
                    <TextInput
                        ref={ref}
                        role="combobox"
                        aria-autocomplete="list"
                        data-testid="select-creatable-input"
                        variant={variant}
                        disabled={disabled}
                        readOnly={isReadOnly}
                        {...otherProps}
                        value={search}
                        aria-busy={isBusy}
                        rightSection={isBusy ? <LoaderIcon size="xs" /> : <DropdownIcon isOpen={isDropdownShown} />}
                        rightSectionPointerEvents="none"
                        onChange={handleChange}
                        onClick={handleClick}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                    />
                </Combobox.Target>
                <Combobox.Dropdown hidden={isDropdownHidden} p="sm" data-testid="select-creatable-dropdown">
                    <Combobox.Options>
                        {/* On a short window the list gives up height first, so the create row stays in view. */}
                        <Stack gap={0} mah={MENU_AVAILABLE_HEIGHT}>
                            {visibleOptions.length === 0 && !isCreateShown && (
                                <Text size="sm" dimmed className={selectMenuStyles["empty"]}>
                                    {noItemsFoundLabel}
                                </Text>
                            )}
                            {visibleOptions.length > 0 && (
                                <ScrollArea.Autosize mah={SEARCHABLE_MENU_MAX_HEIGHT} flex="0 1 auto" type="scroll">
                                    {typeof visibleGroups === "undefined"
                                        ? visibleOptions.map(renderOptionRow)
                                        : visibleGroups.map(renderGroup)}
                                </ScrollArea.Autosize>
                            )}
                            {isCreateShown && (
                                <>
                                    {visibleOptions.length > 0 && <Divider my={DIVIDER_SPACING} />}
                                    <Combobox.Option
                                        value={createOptionValue}
                                        p={MENU_ITEM_PADDING}
                                        bdrs="md"
                                        aria-busy={isCreating}
                                        data-testid="select-creatable-create"
                                    >
                                        <Group gap="sm" justify="space-between" wrap="nowrap">
                                            <Group gap="sm" wrap="nowrap" miw={0}>
                                                {/* The theme's loader ignores the ActionIcon's loader size, so a smaller loader replaces the plus. */}
                                                <ActionIcon
                                                    component="span"
                                                    aria-hidden
                                                    variant="filled"
                                                    radius="xl"
                                                    size="sm"
                                                    icon={isCreating ? undefined : faPlus}
                                                >
                                                    {isCreating && <LoaderIcon size="xs" />}
                                                </ActionIcon>
                                                <Text
                                                    size={ROW_LABEL_SIZE}
                                                    variant="semibold"
                                                    c="var(--mantine-color-text-link)"
                                                    truncate
                                                >
                                                    Create “{trimmedSearch}”
                                                </Text>
                                            </Group>
                                            <Kbd className={styles["select-creatable-kbd"]}>Enter</Kbd>
                                        </Group>
                                    </Combobox.Option>
                                </>
                            )}
                        </Stack>
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        );
    }
);

export { SelectCreatable };
